/**
 * A very small markdown reader, for one thing: what the agent reported.
 *
 * The console renders that report as raw text today, so a run summary reads as
 * `## What I did` and `- **auth.ts**: rewritten`, hashes and asterisks and all.
 * It is the one piece of prose in the product a machine wrote for a person to
 * read, and it arrives in markdown because that is what the agent writes in.
 *
 * What this is not: a markdown implementation. It reads headings, paragraphs,
 * bullet and numbered lists, inline code, fenced code, links, bold and pipe
 * tables, because that is what agent reports and plans contain, and it treats
 * everything else as the text it is. There is no dependency, for the reason the
 * fuzzy matcher has none: the hub serves every byte of this page itself.
 *
 * The safety rule is structural rather than a filter. This produces data, never
 * markup: the renderer builds React elements from it, so anything that is not a
 * span this parser recognises can only ever become a text node. There is no
 * `dangerouslySetInnerHTML` anywhere in the path, so `<img onerror=...>` in a
 * report is five words on the screen. The one place a string does reach the DOM
 * as anything but text is a link's `href`, which is why `safeHref` exists and
 * why a link it refuses degrades to its own label.
 */

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong'; text: string }
  | { type: 'link'; text: string; href: string };

/** A cell's alignment, from the delimiter row; `null` is the default, left. */
export type TableAlign = 'left' | 'center' | 'right' | null;

export type Block =
  | { type: 'heading'; level: 1 | 2 | 3; spans: Inline[] }
  | { type: 'paragraph'; spans: Inline[] }
  | { type: 'list'; ordered: boolean; items: Inline[][] }
  | { type: 'code'; text: string }
  | { type: 'table'; align: TableAlign[]; header: Inline[][]; rows: Inline[][][] };

/**
 * The schemes a link may carry. `javascript:` and `data:` are the two that turn
 * a link into script, and a relative href in a report means nothing anyway - the
 * agent was writing about a repo, not about this page - so the allowed set is
 * the three that can only ever navigate.
 */
const SAFE_SCHEME = /^(https?:|mailto:)/i;

export function safeHref(raw: string): string | null {
  const trimmed = raw.trim();
  // Anything outside printable ASCII is refused outright rather than stripped:
  // `java\nscript:alert(1)` is the oldest trick there is and it survives a naive
  // prefix test, and a report's link is a URL an agent copied out of a terminal.
  if (/[^\u0021-\u007e]/.test(trimmed)) return null;
  return SAFE_SCHEME.test(trimmed) ? trimmed : null;
}

/**
 * The three inline spans, each bounded twice: to its own line, and to a length
 * no real span exceeds.
 *
 * Both bounds are about cost rather than taste. A class that can cross a newline
 * rescans the whole rest of the report from every candidate opener, so a run
 * summary of 60,000 unclosed `[` took 1.3 seconds of the main thread - and a
 * report is written by a model, which makes it the one string on this page that
 * an outsider's prompt can shape. Bounded, the same input parses in under a
 * millisecond. A link label past 200 characters or a code span past 500 is not a
 * span anybody wrote, and stays the text it is.
 */
const CODE = /`([^`\n]{1,500})`/;
// The href allows one level of balanced parentheses, because half of Wikipedia
// is `/wiki/Mercury_(planet)` and cutting the href at the first `)` both broke
// the link and spat `) ...` into the middle of the sentence.
const LINK = /\[([^\]\n]{0,200})\]\(((?:[^()\s]|\([^()\s]*\)){0,500})\)/;
const STRONG = /\*\*([^*\n]{1,200})\*\*/;
const SPAN = new RegExp(`${CODE.source}|${LINK.source}|${STRONG.source}`, 'g');

/**
 * Past this, the report is printed rather than read.
 *
 * It is the same 20,000 characters the hub already accepts a report up to
 * (`TaskReport.summary`), so anything longer did not come through a worker and
 * has no claim on the parse. Printing it is not a degradation a reader will
 * notice - nobody reads 20,000 characters of agent notes in a 400px pane - and
 * it puts a ceiling on the work this function can be asked to do.
 */
export const MARKDOWN_LIMIT = 20_000;

/**
 * One line of text, split into spans. Code wins over everything else because a
 * backtick suppresses markdown inside it, which is exactly what an agent quoting
 * a shell command depends on.
 */
export function parseInline(text: string): Inline[] {
  const spans: Inline[] = [];
  let cursor = 0;
  SPAN.lastIndex = 0;

  let match = SPAN.exec(text);
  while (match) {
    if (match.index > cursor) spans.push({ type: 'text', text: text.slice(cursor, match.index) });
    const [whole, code, label, href, strong] = match;
    if (code !== undefined) {
      spans.push({ type: 'code', text: code });
    } else if (href !== undefined) {
      const safe = safeHref(href);
      // A link nobody should follow keeps its words and loses its target.
      if (safe) spans.push({ type: 'link', text: label || safe, href: safe });
      else spans.push({ type: 'text', text: label || whole });
    } else if (strong !== undefined) {
      spans.push({ type: 'strong', text: strong });
    }
    cursor = match.index + whole.length;
    match = SPAN.exec(text);
  }

  if (cursor < text.length) spans.push({ type: 'text', text: text.slice(cursor) });
  return spans;
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s{0,3}[-*+]\s+(.*)$/;
// One to three digits, so a sentence that opens with a year - "2024. was a year
// of two halves" - is a sentence rather than the first item of an ordered list.
const NUMBERED = /^\s{0,3}\d{1,3}[.)]\s+(.*)$/;
const FENCE = /^\s{0,3}```/;

/**
 * A row is a line with a pipe in it, split on the pipes an agent did not
 * escape. The leading and trailing pipe a table row is usually written with
 * are optional on both sides, because a plan step's own table sometimes drops
 * one or the other and both spellings mean the same row.
 */
function splitRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|') && !trimmed.endsWith('\\|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, '|'));
}

const TABLE_DELIM_CELL = /^:?-{1,}:?$/;

/**
 * The row under a table header, `|---|:---:|---:|`, is what tells this parser
 * a pipe-separated line is a table rather than a sentence that happens to
 * contain one: prose with a stray `|` in it never has a second line of nothing
 * but dashes and colons under it. Returns each column's alignment, or `null`
 * if the line is not one.
 */
function tableDelimiter(line: string): TableAlign[] | null {
  if (!line.includes('-')) return null;
  const cells = splitRow(line);
  if (!cells.length) return null;
  const aligns: TableAlign[] = [];
  for (const cell of cells) {
    if (!TABLE_DELIM_CELL.test(cell)) return null;
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    aligns.push(left && right ? 'center' : right ? 'right' : left ? 'left' : null);
  }
  return aligns;
}

export function parseMarkdown(source: string): Block[] {
  // Too long to be a report anybody wrote: one paragraph, no inline pass, and
  // therefore no work proportional to anything but the length itself.
  if (source.length > MARKDOWN_LIMIT) {
    return [{ type: 'paragraph', spans: [{ type: 'text', text: source }] }];
  }

  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? '';

    if (FENCE.test(line)) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index] ?? '')) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      // An unclosed fence still ends: the rest of the report is the block.
      index += 1;
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    if (!line.trim()) {
      index += 1;
      continue;
    }

    // A table only commits once the delimiter row confirms it; a header row
    // that turns out to be a plain sentence with a `|` in it falls through to
    // the paragraph case below, unparsed.
    if (line.includes('|')) {
      const aligns = index + 1 < lines.length ? tableDelimiter(lines[index + 1] ?? '') : null;
      if (aligns) {
        const header = splitRow(line).map((cell) => parseInline(cell));
        index += 2;
        const rows: Inline[][][] = [];
        while (index < lines.length) {
          const current = lines[index] ?? '';
          if (!current.trim() || !current.includes('|')) break;
          rows.push(splitRow(current).map((cell) => parseInline(cell)));
          index += 1;
        }
        blocks.push({ type: 'table', align: aligns, header, rows });
        continue;
      }
    }

    const heading = HEADING.exec(line);
    if (heading) {
      // Three levels, not six: this renders inside a 400px pane, and a report
      // that nests deeper than three is not going to be read by outline anyway.
      const level = Math.min(3, heading[1]?.length ?? 1) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, spans: parseInline(heading[2] ?? '') });
      index += 1;
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      const items: Inline[][] = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const next = ordered ? NUMBERED.exec(current) : BULLET.exec(current);
        if (!next) break;
        items.push(parseInline(next[1] ?? ''));
        index += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? '';
      const nextIsTableDelimiter =
        current.includes('|') &&
        index + 1 < lines.length &&
        tableDelimiter(lines[index + 1] ?? '') !== null;
      if (
        !current.trim() ||
        FENCE.test(current) ||
        HEADING.test(current) ||
        BULLET.test(current) ||
        NUMBERED.test(current) ||
        nextIsTableDelimiter
      ) {
        break;
      }
      paragraph.push(current);
      index += 1;
    }
    // Joined with the newlines it was written with rather than reflowed into one
    // line: an agent that wrote three short lines meant three lines, and the
    // renderer keeps them with `whitespace-pre-wrap`.
    blocks.push({ type: 'paragraph', spans: parseInline(paragraph.join('\n')) });
  }

  return blocks;
}
